/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T00:30:00+05:00
Intent: One status per participant and per level on a campaign's details. The server can keep a participant at
"not_started" after their first attempt began (their levels already read "in_progress"), and its level metrics
count those participants as not started; this reads the levels themselves so the Participants table, its filter,
the leaderboard, the levels drawer and the Level Matrix all agree.
*/

export type ProgressStatus = 'completed' | 'in_progress' | 'not_started';

const NOT_STARTED_CODES = new Set(['', 'not_started', 'notstarted', 'pending', 'joined']);

/** A level or participant status as one of the three states; anything unknown counts as not started. */
export function toProgressStatus(value: any): ProgressStatus {
  const key = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (key === 'completed' || key === 'complete' || key === 'done') return 'completed';
  if (key === 'in_progress' || key === 'inprogress' || key === 'started' || key === 'active') return 'in_progress';
  return 'not_started';
}

function levelDetails(participant: any): any[] {
  const details = participant?.levels_detail ?? participant?.levelsDetail;
  return Array.isArray(details) ? details : [];
}

/** `levels_detail` carries the level's id as `id`; `level_id` is the older name. */
function levelIdOf(level: any): string {
  return String(level?.level_id ?? level?.id ?? '');
}

/**
 * Each level's state for one participant, by level id. Levels are played in order, so only the first level not yet
 * completed can be in progress; the server marks every unfinished level "in_progress", which would otherwise show a
 * participant on level 1 as in progress on all of them. `orderedLevelIds` is the campaign's level order; without
 * it the order of `levels_detail` is used.
 */
export function effectiveLevelStatuses(participant: any, orderedLevelIds: string[] = []): Map<string, ProgressStatus> {
  const details = levelDetails(participant);
  const byId = new Map(details.map((detail) => [levelIdOf(detail), detail]));
  const order = orderedLevelIds.length ? orderedLevelIds.map(String) : details.map(levelIdOf);
  const participantStarted = toProgressStatus(participant?.status ?? participant?.participant_status) === 'in_progress';

  const statuses = new Map<string, ProgressStatus>();
  let currentFound = false;
  for (const id of order) {
    if (!id) continue;
    const detail = byId.get(id);
    const status = toProgressStatus(detail?.status);
    if (status === 'completed') {
      statuses.set(id, 'completed');
    } else if (!currentFound) {
      currentFound = true;
      statuses.set(id, status === 'in_progress' || (!detail && participantStarted) ? 'in_progress' : 'not_started');
    } else {
      statuses.set(id, 'not_started');
    }
  }
  return statuses;
}

/**
 * The participant's state on the campaign. A participant the server still calls "not started" (or pending / blank)
 * whose levels show a started or completed level, or who has completed levels, is in progress. "completed" and
 * "in_progress" from the server are kept as they are.
 */
export function effectiveParticipantStatus(participant: any, orderedLevelIds: string[] = []): string {
  const raw = String(participant?.status ?? participant?.participant_status ?? '').trim().toLowerCase();
  const stored = toProgressStatus(raw);
  if (stored !== 'not_started') return stored;
  // Any other state the server sets (e.g. a disqualified participant) is left alone.
  if (!NOT_STARTED_CODES.has(raw.replace(/[\s-]+/g, '_'))) return raw;

  const started = Number(participant?.levels_completed ?? participant?.levelsCompleted ?? 0) > 0 ||
    [...effectiveLevelStatuses(participant, orderedLevelIds).values()].some((status) => status !== 'not_started');
  return started ? 'in_progress' : (raw || 'not_started');
}

/** A breakdown with a single group holds the whole level, so it can take the level's corrected counts as they are. */
function reconcileBreakdown(entries: any, pool: number, inProgress: number, notStarted: number): any {
  if (!Array.isArray(entries) || entries.length !== 1) return entries;
  const entry = entries[0] || {};
  if (Number(entry.not_started ?? 0) + Number(entry.in_progress ?? 0) !== pool) return entries;
  return [{ ...entry, in_progress: inProgress, not_started: notStarted }];
}

/**
 * The Level Matrix with each level's in-progress participants counted from the participants' own levels. Only
 * participants the server counted as not started move to in progress (completed counts and the completion rate are
 * the server's), so a participants list that is only part of the campaign can never over-count.
 */
export function reconcileLevelMatrix(matrix: any, participants: any[], orderedLevelIds: string[] = []): any {
  if (!matrix || !Array.isArray(matrix.levels) || !Array.isArray(participants) || participants.length === 0) {
    return matrix;
  }

  const inProgressByLevel = new Map<string, number>();
  for (const participant of participants) {
    effectiveLevelStatuses(participant, orderedLevelIds).forEach((status, id) => {
      if (status === 'in_progress') inProgressByLevel.set(id, (inProgressByLevel.get(id) ?? 0) + 1);
    });
  }

  let changed = false;
  const levels = matrix.levels.map((level: any) => {
    const serverInProgress = Number(level?.in_progress_count ?? 0);
    const serverNotStarted = Number(level?.not_started_count ?? 0);
    const pool = serverInProgress + serverNotStarted;
    const inProgress = Math.min(pool, Math.max(serverInProgress, inProgressByLevel.get(levelIdOf(level)) ?? 0));
    if (inProgress === serverInProgress) return level;

    changed = true;
    const notStarted = pool - inProgress;
    return {
      ...level,
      in_progress_count: inProgress,
      not_started_count: notStarted,
      by_country: reconcileBreakdown(level.by_country, pool, inProgress, notStarted),
      by_device_type: reconcileBreakdown(level.by_device_type, pool, inProgress, notStarted),
      by_gender: reconcileBreakdown(level.by_gender, pool, inProgress, notStarted),
      by_age_group: reconcileBreakdown(level.by_age_group, pool, inProgress, notStarted),
    };
  });

  return changed ? { ...matrix, levels } : matrix;
}
